const emptySerializedErrorMessages = ['{}', '[]']
const unknownObjectErrorMessage = '[object Object]'
const fallbackUnknownErrorMessage = 'Unknown error'
const unknownErrorPropertyNames = ['name', 'message', 'code', 'reason']

export function normalizeUnknownError(error: unknown): Error {
  if (error instanceof Error) {
    return error
  }

  if (typeof error === 'string') {
    return new Error(error)
  }

  return new Error(getUnknownErrorMessage(error))
}

export function isEmptyUnknownError(error: unknown) {
  if (!error || typeof error !== 'object' || error instanceof Error) {
    return false
  }

  const hasSerializedMessage = Boolean(getSerializedUnknownErrorMessage(error))
  const hasPropertyMessage = Boolean(getPropertyUnknownErrorMessage(error))
  const hasStringMessage = Boolean(getStringUnknownErrorMessage(error))

  return !hasSerializedMessage && !hasPropertyMessage && !hasStringMessage
}

function getUnknownErrorMessage(error: unknown) {
  return (
    getSerializedUnknownErrorMessage(error) ||
    getPropertyUnknownErrorMessage(error) ||
    getStringUnknownErrorMessage(error) ||
    fallbackUnknownErrorMessage
  )
}

function getSerializedUnknownErrorMessage(error: unknown) {
  try {
    const serializedError = JSON.stringify(error)

    if (
      !serializedError ||
      emptySerializedErrorMessages.includes(serializedError)
    ) {
      return
    }

    return serializedError
  } catch {
    return
  }
}

function getPropertyUnknownErrorMessage(error: unknown) {
  if (!error || typeof error !== 'object') {
    return
  }

  const values = unknownErrorPropertyNames
    .map((propertyName) => getUnknownErrorProperty(error, propertyName))
    .filter(Boolean)

  if (values.length === 0) {
    return
  }

  return values.join(': ')
}

function getUnknownErrorProperty(error: object, propertyName: string) {
  try {
    const value = (error as Record<string, unknown>)[propertyName]

    if (['number', 'boolean'].includes(typeof value)) {
      return String(value)
    }

    if (typeof value !== 'string' || !value.trim()) {
      return
    }

    return value
  } catch {
    return
  }
}

function getStringUnknownErrorMessage(error: unknown) {
  try {
    const stringError = String(error)

    if (!stringError || stringError === unknownObjectErrorMessage) {
      return
    }

    return stringError
  } catch {
    return
  }
}

export function getErrorMessage(error: unknown, fallback = '') {
  if (error === undefined || error === null) {
    return fallback
  }

  const responseError = (
    error as {
      response?: {
        data?: {
          error?: unknown
        }
      }
    }
  )?.response?.data?.error

  if (typeof responseError === 'string' && responseError) {
    return responseError
  }

  return normalizeUnknownError(error).message
}
